<template>
  <div class="sgt-builder-usage-opt">
    <!-- All / Any of -->
    <v-select
      v-model="isOrLogic"
      variant="outlined"
      density="compact"
      hideDetails
      bgColor="#ffffff"
      persistentPlaceholder
      height="32px"
      class="w-22"
      itemTitle="text"
      :items="LogicItems"
      :disabled="disabled"
      :menuProps="{ offsetY: true }"
      @update:modelValue="updateFilter"
    />
    <RfChevronIcon class="fill-beige-50 mx-1 my-auto h-6 w-6 rotate-180" />
    <!-- Tracker -->
    <v-autocomplete
      v-model="parentModels"
      variant="outlined"
      density="compact"
      hideDetails
      chips
      multiple
      class="segment-builder--subtypes _subtypes min-w-42"
      persistentPlaceholder
      bgColor="#ffffff"
      placeholder="Select configuration"
      itemTitle="name"
      itemValue="label"
      :disabled="disabled"
      :items="usageItems"
      :itemDisabled="parentModelItemDisabled"
      :menuProps="{ maxHeight: 340, offsetY: true, minWidth: 240, maxWidth: 400 }"
      @update:modelValue="didChangeParentModels"
    >
      <template #item="{ item, props: itemProps }">
        <v-tooltip location="right" :disabled="!parentModelItemDisabled(item.raw)" openDelay="500">
          <template #activator="{ props }">
            <v-list-item v-bind="{ ...props, ...itemProps }">
              <template #prepend="{ isSelected }">
                <v-checkbox
                  hideDetails
                  class="ml-n2 mt-0 pt-0"
                  readonly
                  :modelValue="isSelected"
                />
              </template>
            </v-list-item>
          </template>
          <span> "Concurrent Logins" needs to be a standalone row </span>
        </v-tooltip>
      </template>
    </v-autocomplete>
    <!-- Range Type -->
    <div v-if="parentModels.length" class="segment-builder--item next-selection">
      <RfChevronIcon class="fill-beige-50 mx-1 my-auto h-6 w-6 rotate-180" />
      <v-select
        variant="outlined"
        density="compact"
        hideDetails
        bgColor="#ffffff"
        persistentPlaceholder
        placeholder="Match Type"
        itemTitle="text"
        height="32px"
        class="min-w-28"
        :items="allRangeTypes"
        :itemDisabled="rangeTypeItemDisabled"
        :disabled="disabled"
        :menuProps="{ offsetY: true }"
        :modelValue="rangeType"
        @update:modelValue="didChangeRangeType"
      >
        <template #item="{ item, props: itemProps }">
          <v-tooltip :disabled="!rangeTypeItemDisabled(item.raw)" right openDelay="500">
            <template #activator="{ props }">
              <v-list-item class="pointer-events-auto" v-bind="{ ...props, ...itemProps }" />
            </template>
            <span v-if="item.value === 'none'">
              "None" does not apply for "Visits" or "Minutes".<br />
              Create a separate row for custom trackers to match "None"
            </span>
            <span v-if="item.value === 'trending'">
              "Trending" cannot be used for "Concurrent Logins"
            </span>
          </v-tooltip>
        </template>
      </v-select>
    </div>
    <!-- Values -->
    <div v-if="!!RangeTypesBase[rangeType]" class="flex items-center">
      <RfChevronIcon class="fill-beige-50 mx-1 my-auto h-6 w-6 rotate-180" />
      <v-text-field
        v-model="value1"
        variant="outlined"
        density="compact"
        hideDetails
        bgColor="#ffffff"
        persistentPlaceholder
        class="w-20"
        :placeholder="rangeType === 'range' ? 'From' : 'e.g.: 12'"
        :disabled="disabled"
        @update:modelValue="updateFilter"
      />
    </div>
    <div v-if="rangeType === 'range'" class="flex">
      <span class="px-2">-</span>
      <v-text-field
        v-model="value2"
        variant="outlined"
        density="compact"
        hideDetails
        bgColor="#ffffff"
        persistentPlaceholder
        class="w-20"
        placeholder="To"
        :disabled="disabled"
        @update:modelValue="updateFilter"
      />
    </div>
    <!-- Time period -->
    <div
      v-if="
        !parentModels.includes('concurrent_logins') &&
        rangeType &&
        !['none', 'trending'].includes(rangeType)
      "
      class="flex items-center"
    >
      <RfChevronIcon class="fill-beige-50 mx-1 my-auto h-6 w-6 rotate-180" />
      <span class="pr-3 whitespace-nowrap">Over the last</span>
      <v-select
        v-model="timePeriod"
        variant="outlined"
        density="compact"
        hideDetails
        bgColor="#ffffff"
        itemTitle="text"
        persistentPlaceholder
        height="32px"
        class="w-24"
        placeholder="Days"
        :items="Object.values(RawUsageTimePeriods)"
        :disabled="disabled"
        :menuProps="{ offsetY: true }"
        @update:modelValue="updateFilter"
      />
    </div>
    <!-- Trending -->
    <div v-if="rangeType === 'trending'" class="flex items-start py-1">
      <RfChevronIcon class="fill-beige-50 mx-1 my-auto h-6 w-6 rotate-180" />
      <div class="ml-1 flex flex-col gap-4">
        <div class="flex">
          <div class="mr-3 ml-1">Range:</div>
          <RfRangeSlider
            v-model="trendRange"
            :min="0"
            :max="10"
            :step="1"
            :disabled="disabled"
            :isNormalized="true"
            @update:modelValue="updateFilter"
          />
        </div>
        <div class="flex gap-2">
          <v-select
            v-model="trendDirection"
            variant="outlined"
            density="compact"
            hideDetails
            bgColor="#ffffff"
            persistentPlaceholder
            placeholder="Trend Direction"
            itemTitle="text"
            class="w-44"
            height="32px"
            :items="allTrendDirections"
            :disabled="disabled"
            @update:modelValue="updateFilter"
          />
          <v-select
            v-model="trendDuration"
            variant="outlined"
            density="compact"
            hideDetails
            bgColor="#ffffff"
            persistentPlaceholder
            placeholder="Time Period"
            itemTitle="text"
            class="w-44"
            height="32px"
            :items="allTrendDurations"
            :disabled="disabled"
            @update:modelValue="updateFilter"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { getUsageFilterData } from "@/utils/segmentsHelpers";
import { useCustomFields } from "@/utils/composables/useCustomFields";
import { onMounted, ref } from "vue";
import {
  RangeTypes,
  RangeTypesBase,
  RawUsageTimePeriods,
  LogicItems,
} from "@/utils/constants/SegmentsConstants";
import RfRangeSlider from "@/components/RfRangeSlider.vue";
import RfChevronIcon from "../icons/RfChevronIcon.vue";

const props = defineProps(["model", "isInOrGroups", "disabled"]);
const emit = defineEmits(["update:model", "updateOrGroups"]);

const customFields = useCustomFields();
const usageItems = getUsageFilterData(customFields.appCustomFields.value);

const allRangeTypes = Object.values(RangeTypes);
const allTrendDirections = [
  { value: "trending_up", text: "Trending Up" },
  { value: "trending_down", text: "Trending Down" },
  { value: "any", text: "Any Direction" },
];
const allTrendDurations = [
  { value: "monthly", text: "Month over month" },
  { value: "weekly", text: "Week over week" },
  { value: "daily", text: "Day over day" },
];

const isOrLogic = ref(false);
const parentModels = ref([]);
const rangeType = ref();
const value1 = ref();
const value2 = ref();
const timePeriod = ref();
const trendRange = ref([0, 10]);
const trendDirection = ref();
const trendDuration = ref();

const resetValues = () => {
  value1.value = undefined;
  value2.value = undefined;
  timePeriod.value = undefined;
  trendRange.value = [0, 10];
  trendDirection.value = undefined;
  trendDuration.value = undefined;
};

const didChangeParentModels = to => {
  if (rangeTypeItemDisabled({ value: rangeType.value }) || !to.length) {
    rangeType.value = undefined;
  }
  if (!to.length) {
    resetValues();
  }
  updateFilter();
};
const didChangeRangeType = toValue => {
  const fromValue = rangeType.value;
  rangeType.value = toValue;
  if (!RangeTypesBase[fromValue] || !RangeTypesBase[toValue]) resetValues();
  updateFilter();
};

const parentModelItemDisabled = ({ label }) => {
  return (
    (parentModels.value.includes("concurrent_logins") && label !== "concurrent_logins") ||
    (parentModels.value.length > 0 &&
      !parentModels.value.includes("concurrent_logins") &&
      label === "concurrent_logins")
  );
};
const rangeTypeItemDisabled = ({ value }) => {
  if (value === "none")
    return parentModels.value.includes("minutes") || parentModels.value.includes("visits");
  if (value === "trending") return parentModels.value.includes("concurrent_logins");
  return false;
};

const valuesForRangeType = () => {
  switch (rangeType.value) {
    case "none":
      return ["0", "0"];
    case "at_least_once":
      return ["1", "INF"];
    case "equal_to":
      return [value1.value, value1.value];
    case "less_than_or_equal_to":
      return ["0", value1.value];
    case "greater_than_or_equal_to":
      return [value1.value, "INF"];
    case "range":
      return [value1.value, value2.value];
    case "trending":
      return trendRange.value.map(String);
  }
};
const updateFilter = () => {
  const filters = {
    usage: parentModels.value.reduce(
      (obj, parentModel) => ({
        ...obj,
        [parentModel]: {
          options: {
            trend: trendDirection.value,
            frequency: trendDuration.value,
            range_type: rangeType.value,
            time_period_days: timePeriod.value,
          },
          values: valuesForRangeType(),
        },
      }),
      {},
    ),
  };
  emit("update:model", filters);
  emit("updateOrGroups", isOrLogic.value, parentModels.value);
};

onMounted(() => {
  const { usage } = props.model;
  parentModels.value = Object.keys(usage);
  if (!parentModels.value.length) return;

  let { options, values } = usage[parentModels.value[0]];
  trendDirection.value = options.trend;
  trendDuration.value = options.frequency;
  rangeType.value = options.range_type;
  timePeriod.value = options.time_period_days;
  isOrLogic.value = props.isInOrGroups;
  if (values) {
    if (options.range_type === "less_than_or_equal_to") {
      value1.value = values[1];
    } else {
      [value1.value, value2.value] = values;
    }
    trendRange.value = values;
  }
});
</script>

<style lang="scss" scoped>
:deep(.v-autocomplete) .v-select__selections > .v-chip {
  margin: 2px 4px;

  ~ input {
    min-width: 20px;
    max-width: 20px;
  }
}
</style>
